import { FC, useEffect } from "react";
import { UseFormReturn, useWatch } from "react-hook-form";
import { makeStyles } from "tss-react/mui";
import { MiPlaceholderList } from "@miplatform/components";
import { ITransferListItem } from "@miplatform/types";
import { TransferList } from "@mibuildweb/components";
import { Box, Tab, Tabs, Typography } from "@mui/material";
import { ConfigAccordionHeader } from "../../../../../Config/components/ConfigAccordionHeader/ConfigAccordionHeader";
import { IAddRequirementsData } from "../../schema/addRequirements.schema";
import { useRequirementStepsViewModel } from "./useRequirementStepsViewModel";

export const RequirementStepsTab: FC<RequirementStepsTabProps> = ({
  form,
  extraTabProps,
}) => {
  const { isReadOnly = false } = extraTabProps || {};
  const { classes, cx } = useStyles();
  const { control, setValue } = form;

  const steps =
    (useWatch({ control, name: "steps" }) as {
      stepTemplateId: string;
      workBaseTemplateId: string;
    }[]) ?? [];

  const {
    orderTemplateTabs,
    allSteps,
    selectedTab,
    loadTabs,
    getSelectedStepIds,
    handleAddStep,
    handleRemoveStep,
    handleTabChange,
  } = useRequirementStepsViewModel({ steps, setValue });

  useEffect(() => {
    loadTabs();
  }, []);

  const selectedStepIds = selectedTab ? getSelectedStepIds(selectedTab.id) : [];

  const availableStepItems: ITransferListItem[] = allSteps
    .filter((s) => !selectedStepIds.includes(s.id))
    .map((s) => ({ key: s.id, title: s.name }));

  const selectedStepItems: ITransferListItem[] = allSteps
    .filter((s) => selectedStepIds.includes(s.id))
    .map((s) => ({ key: s.id, title: s.name }));

  const isEmpty = allSteps.length === 0;

  return (
    <Box className={classes.container}>
      <Box className={cx(classes.inner, { [classes.innerExpanded]: !isEmpty })}>
        <ConfigAccordionHeader title="Requirement Steps" disableCollapse>
          {isEmpty && (
            <Box className={classes.placeholderBox}>
              <MiPlaceholderList
                text="No steps available."
                helperText="Steps must be configured before they can be assigned."
              />
            </Box>
          )}
          {!isEmpty && (
            <>
              <Typography
                variant="subtitle2"
                color="text.secondary"
                sx={{ mb: 1 }}
              >
                Select the steps for this requirement using the Order tabs
                below.
              </Typography>
              {orderTemplateTabs.length > 0 && (
                <Tabs
                  value={selectedTab?.id ?? false}
                  onChange={(_, value) => {
                    const tab = orderTemplateTabs.find((t) => t.id === value);
                    if (tab) handleTabChange(tab);
                  }}
                  sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}
                >
                  {orderTemplateTabs.map((tab) => (
                    <Tab
                      key={tab.id}
                      value={tab.id}
                      label={tab.name}
                      sx={{ textTransform: "none" }}
                    />
                  ))}
                </Tabs>
              )}

              <TransferList
                availableItems={availableStepItems}
                selectedItems={selectedStepItems}
                availableTitle="Available Steps"
                selectedTitle="Selected Steps"
                showAvailableItemsSearch
                availableSearchPlaceholder="Search"
                emptySelectedListMessage="Selected steps will show here."
                onAddItem={(key: string) => handleAddStep(selectedTab!.id, key)}
                onRemoveItem={(key: string) =>
                  handleRemoveStep(selectedTab!.id, key)
                }
                renderItemTitle={(item: ITransferListItem) => (
                  <Typography variant="HD2" color="primary">
                    {item.title}
                  </Typography>
                )}
                disabled={isReadOnly}
              />
            </>
          )}
        </ConfigAccordionHeader>
      </Box>
    </Box>
  );
};

interface RequirementStepsTabProps {
  form: UseFormReturn<IAddRequirementsData>;
  extraTabProps?: {
    isReadOnly?: boolean;
  };
}

const useStyles = makeStyles()((theme) => ({
  container: {
    display: "flex",
    flex: 1,
    height: "100%",
    overflow: "hidden",
  },
  inner: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
  },
  innerExpanded: {
    "& .MuiAccordion-root": {
      flex: 1,
      display: "flex",
      flexDirection: "column",
    },
    "& .MuiAccordionDetails-root": {
      flex: 1,
      overflowY: "auto",
      display: "flex",
      flexDirection: "column",
    },
  },
  placeholderBox: {
    flex: 1,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: theme.spacing(2, 0),
  },
}));
